import { doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { chatMessageCap, utcDayKey, type Plan } from "@amazing-wedding/shared-types";

/**
 * Thrown when the user has burned today's chat budget. Caught in
 * useAIPlanner to render a friendly upgrade nudge instead of fielding
 * the message to Gemini.
 */
export class ChatQuotaExceededError extends Error {
  constructor(
    readonly plan: Plan,
    readonly used: number,
    readonly cap: number,
    readonly isDemo = false,
  ) {
    super(`chat_quota_exceeded`);
    this.name = "ChatQuotaExceededError";
  }
}

/**
 * Reserve one chat message against the daily quota. Runs as a Firestore
 * transaction so concurrent tabs cannot overshoot the cap, and respects
 * the matching rule that only allows monotonic +1 updates from clients.
 *
 * Resolves on success (the counter has been incremented). Rejects with
 * ChatQuotaExceededError when the cap is already reached.
 */
export async function reserveChatMessage(
  uid: string,
  plan: Plan,
  options?: { isDemo?: boolean },
): Promise<void> {
  const isDemo = options?.isDemo ?? false;
  const cap = chatMessageCap({ plan, isDemo });
  const dayRef = doc(db, "users", uid, "dailyUsage", utcDayKey());
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(dayRef);
    const used = snap.exists()
      ? Number((snap.data() as { chatMessages?: number }).chatMessages ?? 0)
      : 0;
    if (used >= cap) {
      throw new ChatQuotaExceededError(plan, used, cap, isDemo);
    }
    if (snap.exists()) {
      tx.update(dayRef, {
        chatMessages: used + 1,
        updatedAt: serverTimestamp(),
      });
    } else {
      tx.set(dayRef, {
        chatMessages: 1,
        updatedAt: serverTimestamp(),
      });
    }
  });
}
